import {
    STC_VARIANT,
    allVariants,
    collection,
    parentTypeFor,
    refs,
    routePath,
} from "../_variants/index.js";
import {
    makeCancelDesignChange,
    makeEditDesignChange,
    makeHideDesignChange,
    makeRevokeDesignChangeCustomerAccess,
    makeShareDesignChangeWithCustomer,
    makeUnhideDesignChange,
} from "./actions.js";
import { stcs as stcAttachable, stcVals as stcValsAttachable } from "../STCs/attachable.js";
import { thirdPartyDocs, thirdPartyDraws, thirdPartyOther } from "../Miscellaneous/attachable.js";

import ItemListView from "../../../components/ItemListView/ItemListView.vue";
import ItemView from "../../../components/ItemView/ItemView.vue";
import TopCompWarning from "./TopCompWarning.vue";
import { makeDesignChangeSummary } from "./internalAttachables.js";
import { appConfig } from "../../../utils";
import arrAttachable from "../Arrangements/attachable.js";
import commercialsAttachable from "../Commercials/attachable.js";
import { createBoxRequest } from "../BoxRequests/actions.js";
import { designChangeItemsReport } from "./reports";
import effectivitiesAttachable from "../Effectivities/attachable.js";
import jobCardsAttachable from "../JobCards/attachable.js";
import { makeConcessionAttachable } from "../Concessions/attachable.js";
import { makeDOIAttachable } from "../DOIs/attachable.js";
import { makeDQNAttachable } from "../DQNs/attachable.js";
import { makeDocumentsAttachable } from "../Documents/attachable.js";
import { makeDrawingAttachable } from "../Drawings/attachable.js";
import { makeEADTAttachable } from "../EADTs/attachable.js";
// import eafAttachable from "../EAF/attachable.js";
import { makeEARAttachable } from "../EARs/attachable.js";
import { makeSADDAttachable } from "../SADDs/attachable.js";
import { makeServiceBulletinAttachable } from "../ServiceBulletins/attachable.js";
import manufacturerAttachable from "../Manufacturers/attachable.js";
import partnumbersAttachable from "../ApprovedPartNumbers/attachable.js";
import sowAttachable from "../SOWs/attachable.js";

const dataFields = [{
    "label": "Number",
    "key": "Number"
}, {
    "label": "Primary Commercial / Job Number",
    "key": "Parent",
    "link": "Database/Commercials/:ParentID"
}, {
    "label": "Responsible Engineer",
    "key": "ResponsibleEngineer"
}, {
    "label": "Customer",
    "key": "Customer"
}, {
    "label": "STC Approval Number",
    "key": "EASANumber"
}, {
    "label": "Mod Type",
    "key": "Type"
}, {
    "label": "Date Created",
    compute({ item }) {
        const d = new Date(item?.Date?.Created);
        return d.toLocaleDateString("en-GB", {
            "year": 'numeric', "month": 'long', "day": 'numeric'
        });
    }
}, {
    "label": "Document Deadline Date",
    compute({ item }) {
        const d = new Date(item?.DocumentDeadlineDate);
        return d.toLocaleDateString("en-GB", {
            "year": 'numeric', "month": 'long', "day": 'numeric'
        });
    },
    "show": () => {
        return appConfig.appPrefix !== "stc"
    }
},{
    "label": "Title",
    "key": "Title",
    "class": "md:col-span-3"
}];

/**
 * Build the list + detail vue-router routes for the Design Change register
 * matching the supplied variant.
 *
 * The detail route's `meta.formMetadata.ParentType` carries the variant's
 * parentBackref (`DesignChanges` or `MilitaryDesignChanges`), which child
 * entities use via `getVariantByParentType()` to resolve their own variant.
 *
 * @param {import("../_variants/index.js").Variant} variant
 */
export const makeDesignChangeRoutes = (variant) => {
    const isMilitary = variant.key === "military";
    const labelSingular = isMilitary ? "Military Design Change" : "Design Change";
    const labelPlural = isMilitary ? "Military Design Changes" : "Design Changes";
    const dcParentType = parentTypeFor(variant, "DesignChanges");
    const listPath = routePath(variant, "DesignChanges");
    
    return [
        {
            "path": listPath,
            "component": { ...ItemListView },
            "name": labelPlural,
            "meta": {
                "app": variant.app,
                "name": labelPlural,
                "sideNavSection": "Designs",
                "sideNav": true,
                "appSwitcher": true,
                "appSwitcherPosition": variant.appSwitcherPosition,
                "security": variant.security,
                "icon": variant.appIcon,
                "variant": variant.key,
                // Per-variant tableSettings key so users can configure column
                // widths independently for each project.
                "tableSettings": dcParentType,
                "dataPath": refs(variant, "DesignChanges"),
                // Server-side Elasticsearch list mode. Dormant until indices are
                // backfilled and `useElasticsearchLists` is enabled in appConfig.
                // Only the STC variant is indexed (Military collections are not).
                ...(appConfig.useElasticsearchLists && variant.collectionPrefix === "" ? { "esType": "DesignChanges" } : {}),
                "selectionRoute": `${dcParentType === "DesignChanges" ? "Database/DesignChanges" : `Database/${dcParentType}`}/{ID}`,
                // "additionalTableHeaders": [{
                //     "headerName": "Customer Access",
                //     "field": "CustomerAccess.Enabled",
                //     "type": "bool",
                //     "width": 150
                // }],
                "storeData": ["Database/DocumentTypes"],
            }
        },
        {
            "path": `${listPath}/:id`,
            "component": ItemView,
            "name": labelSingular,
            "meta": {
                "app": variant.app,
                "name": labelPlural,
                "dataPath": collection(variant, "DesignChanges"),
                "selectionRoute": `Database/${dcParentType}/{ID}`,
                "storeData": ["Database/DocumentTypes"],
                title(item) {
                    return `${labelPlural}: ${item.Number}`;
                },
                "topComponent": TopCompWarning,
                "topComponentEvent": () => {
                    console.log("Top Component Event");
                },
                "menuActions": [
                    {
                        "label": "Download Design Change Report",
                        "action": designChangeItemsReport
                    },
                    {
                        "label": `Edit ${labelSingular}`,
                        "action": makeEditDesignChange(variant),
                        "break": true
                    },
                    {
                        "label": "Share with Customer",
                        "action": makeShareDesignChangeWithCustomer(variant),
                        "break": true,
                        show({ pageItem }) {
                            return [pageItem.CustomerAccess?.Enabled !== true, "Customer access is already enabled"];
                        }
                    },
                    {
                        "label": "Revoke Customer Access",
                        "action": makeRevokeDesignChangeCustomerAccess(variant),
                        show({ pageItem }) {
                            return [pageItem.CustomerAccess?.Enabled === true, "Customer access is not enabled"];
                        }
                    },
                    {
                        "label": `Cancel ${labelSingular}`,
                        "class": "text-red hover:bg-red",
                        "color": "red",
                        "action": makeCancelDesignChange(variant),
                        "break": true
                    },
                    {
                        "label": "Create Box Request",
                        "action": createBoxRequest,
                        "break": true
                    },
                    {
                        "label": `Hide ${labelSingular}`,
                        "break": true,
                        "action": makeHideDesignChange(variant),
                        show({ pageItem }) {
                            return [!pageItem.Hidden, ''];
                        }
                    },
                    {
                        "label": `Unhide ${labelSingular}`,
                        "action": makeUnhideDesignChange(variant),
                        show({ pageItem }) {
                            return [pageItem.Hidden, ''];
                        }
                    },
                    {
                        "label": "View in Archive",
                        "break": true,
                        action({ pageItem }) {
                            window.open(`https://stc21.sharepoint.com/sites/archive/Shared%20Documents/Forms/AllItems.aspx?id=%2Fsites%2Farchive%2FShared%20Documents%2FDesign Changes/${pageItem.ATAChapter}/S21_${pageItem.Number} ${pageItem.Title}`);
                        }
                    }
                ],
                "attachedItems": {
                    "Design Items": {
                        "attachables": [makeDocumentsAttachable(variant), makeDrawingAttachable(variant), makeSADDAttachable(variant), effectivitiesAttachable]
                    },
                    "Equipment Assessments": {
                        "attachables": [makeEARAttachable(variant), makeEADTAttachable(variant), /* eafAttachable, */ partnumbersAttachable, manufacturerAttachable]
                    },
                    "Design Queries": {
                        "attachables": [makeConcessionAttachable(variant), makeDQNAttachable(variant), makeServiceBulletinAttachable(variant), makeDOIAttachable(variant)]
                    },
                    "Project Items": {
                        "attachables": [
                            commercialsAttachable,
                            jobCardsAttachable,
                            sowAttachable,
                            stcAttachable,
                            stcValsAttachable,
                            arrAttachable,
                            thirdPartyDocs,
                            thirdPartyDraws,
                            thirdPartyOther
                        ]
                    },
                    "AI Tools": {
                        "attachables": [makeDesignChangeSummary(variant)]
                    }
                },
                "dataFields": dataFields,
                "formMetadata": {
                    "ParentType": dcParentType
                }
            }
        }
    ];
};

// Default export: routes for every enabled variant. Builds without a
// `militaryDocPrefix` (DOA, Sigmatmod) automatically receive STC routes only.
export default allVariants().flatMap(makeDesignChangeRoutes);

// Convenience re-export for callers that explicitly want only the STC routes
// (e.g. legacy imports).
export const stcDesignChangeRoutes = makeDesignChangeRoutes(STC_VARIANT);
